import { z } from 'zod';
import { capabilities } from '../types';

const capabilityShape = Object.fromEntries(
  capabilities.map((capability) => [capability, z.boolean().optional().default(false)]),
) as Record<(typeof capabilities)[number], z.ZodDefault<z.ZodOptional<z.ZodBoolean>>>;

export const capabilitiesSchema = z.object(capabilityShape).loose();

export const userSchema = z.object({
  id: z.number(),
  username: z.string(),
  caps: capabilitiesSchema,
  name: z.string().default(''),
  role: z.string().default(''),
  org: z.boolean().default(false),
  orgOwner: z.string().nullable().default(null),
}).loose();

export const authMeSchema = z.union([
  z.object({ authenticated: z.literal(false), needsSetup: z.boolean().default(false) }).loose(),
  z.object({ authenticated: z.literal(true), user: userSchema }).loose(),
]);

export const authResultSchema = z.object({
  ok: z.boolean().optional(),
  authed: z.boolean().optional(),
  need2fa: z.boolean().optional(),
  error: z.string().optional(),
  user: userSchema.partial().optional(),
}).loose();

export const okSchema = z.object({ ok: z.boolean().optional() }).loose();

export const accountSchema = z.object({
  username: z.string(),
  twoFactorEnabled: z.boolean(),
}).loose();

const booleanValueSchema = z.union([z.boolean(), z.number()]).transform(Boolean);
const nullableTextSchema = z.string().nullable().default(null);
const epochMillisecondsSchema = z.preprocess((value) => {
  if (value instanceof Date) return value.getTime();
  if (typeof value !== 'string') return value;

  const trimmed = value.trim();
  if (!trimmed) return Number.NaN;

  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? numeric : Date.parse(trimmed);
}, z.number().finite());
const nullableEpochSchema = epochMillisecondsSchema.nullable().default(null);
const countSchema = z.coerce.number().int().nonnegative();

export const twoFactorSetupSchema = z.object({
  secret: z.string(),
  otpauthUrl: z.string(),
  qr: z.string().nullable(),
}).loose();

export const eventSchema = z.object({
  id: z.coerce.number().int(),
  zoom_account: z.coerce.number().int().default(1),
  event_type: z.enum(['meeting', 'webinar']).default('meeting'),
  zoom_id: z.union([z.string(), z.number()]).transform(value => String(value)).default(''),
  topic: nullableTextSchema,
  room_name: nullableTextSchema,
  status: nullableTextSchema,
  active: booleanValueSchema.default(false),
  auto_discovered: booleanValueSchema.default(false),
  color: z.string().default('#6b7280'),
  is_vip: booleanValueSchema.default(false),
  recall_bot_id: z.string().nullable().transform(value => value || '').default(''),
  join_url: z.string().nullable().transform(value => value || '').default(''),
  bot_enabled: booleanValueSchema.default(true),
  bot_name: z.string().nullable().transform(value => value || 'ModIQ').default('ModIQ'),
  team_shared: booleanValueSchema.default(true),
  scheduled_at: nullableEpochSchema,
  created_at: nullableEpochSchema,
  last_seen: nullableEpochSchema,
}).loose();

export const eventsSchema = z.array(eventSchema);

export const eventVerificationSchema = z.object({
  ok: booleanValueSchema,
  status: nullableTextSchema,
  topic: nullableTextSchema,
  reason: nullableTextSchema,
}).loose();

export const eventMutationResultSchema = z.object({
  event: eventSchema,
  verification: eventVerificationSchema.nullable().default(null),
}).loose();

export const eventToggleResultSchema = z.object({
  id: z.coerce.number().int(),
  active: booleanValueSchema,
  bot: nullableTextSchema,
}).loose();

export const eventArchiveResultSchema = z.object({
  id: z.coerce.number().int(),
  archived: booleanValueSchema,
}).loose();

export const zoomConnectionStatusSchema = z.object({
  granted: booleanValueSchema.default(false),
  grantedFrom: nullableTextSchema,
  accounts: z.array(z.object({
    account: z.coerce.number().int(),
    configured: booleanValueSchema.default(false),
    ok: booleanValueSchema.default(false),
    reason: nullableTextSchema,
  })),
});

// Setup responses intentionally strip unknown fields so credential values can
// never become part of the typed browser state if a server response regresses.
export const zoomSetupConfigSchema = z.object({
  granted: booleanValueSchema.default(false),
  grantedFrom: nullableTextSchema,
  webhookPath: z.string().startsWith('/'),
  accounts: z.array(z.object({
    index: z.coerce.number().int().min(1).max(2),
    accountId: z.string().default(''),
    clientId: z.string().default(''),
    clientSecretSet: booleanValueSchema.default(false),
    webhookSecretSet: booleanValueSchema.default(false),
  })),
});

export const recallConfigSchema = z.object({
  platform: booleanValueSchema.default(false),
  configured: booleanValueSchema.default(false),
  region: z.string().default('us-east-1'),
  own: booleanValueSchema.default(false),
  inherited: booleanValueSchema.default(false),
});

const whopProductConfigSchema = z.object({
  id: z.string(),
  label: z.string().default(''),
});

export const whopConfigSchema = z.object({
  configured: booleanValueSchema.default(false),
  keySet: booleanValueSchema.default(false),
  companyId: z.string().default(''),
  products: z.array(whopProductConfigSchema).default([]),
  statuses: z.array(z.string()).default([]),
});

export const whopConfigSaveSchema = whopConfigSchema.extend({
  test: z.object({
    ok: booleanValueSchema,
    reason: z.string().optional(),
  }),
});

export const whopProbeSchema = z.object({
  products: z.array(whopProductConfigSchema.extend({
    byStatus: z.record(z.string(), z.union([countSchema, z.literal('error')])),
  })),
});

export const messageSummarySchema = z.object({
  deleted: z.union([z.boolean(), z.number()]).optional(),
  answered: z.union([z.boolean(), z.number()]).optional(),
}).loose();

export const messagesSchema = z.array(messageSummarySchema);

const moderationThreadEntrySchema = z.object({
  who: z.enum(['mod', 'attendee']),
  name: z.string().nullable().default(null),
  text: z.string(),
}).loose();

export const moderationMessageSchema = z.object({
  id: z.coerce.number().int(),
  zoom_account: z.coerce.number().int().default(0),
  meeting_id: z.union([z.string(), z.number()]).transform(value => String(value)).default(''),
  room_name: nullableTextSchema,
  sender_name: z.string().nullable().transform(value => value || 'Attendee').default('Attendee'),
  sender_id: z.string().nullable().transform(value => value || '').default(''),
  email: z.string().nullable().transform(value => value || '').default(''),
  message: z.string(),
  timestamp: epochMillisecondsSchema,
  flagged: booleanValueSchema.default(false),
  highlighted: booleanValueSchema.default(false),
  pinned: booleanValueSchema.default(false),
  answered: booleanValueSchema.default(false),
  deleted: booleanValueSchema.default(false),
  created_at: nullableEpochSchema,
  reply: nullableTextSchema,
  thread: z.array(moderationThreadEntrySchema).default([]),
  event_id: z.coerce.number().int().optional(),
}).loose();

export const moderationMessagesSchema = z.array(moderationMessageSchema);

export const chatResetResultSchema = z.object({
  ok: z.literal(true),
  sinceAt: epochMillisecondsSchema,
}).loose();

export const pinQueueItemSchema = z.object({
  pin_id: z.coerce.number().int(),
  message_id: z.coerce.number().int(),
  answered: booleanValueSchema.default(false),
  sender_name: z.string().nullable().transform(value => value || 'Attendee').default('Attendee'),
  email: z.string().nullable().transform(value => value || '').default(''),
  message: z.string(),
  room_name: nullableTextSchema,
  timestamp: epochMillisecondsSchema,
  zoom_account: z.coerce.number().int().default(0),
  highlighted: booleanValueSchema.default(false),
  meeting_id: z.union([z.string(), z.number()]).transform(value => String(value)).default(''),
}).loose();

export const pinQueueSchema = z.array(pinQueueItemSchema);

export const snippetLibrarySchema = z.object({
  snippets: z.array(z.object({
    id: z.coerce.number().int(),
    label: z.string(),
    body: z.string(),
    uses: countSchema,
    created_name: nullableTextSchema,
    created_by: z.coerce.number().int().nullable().default(null),
  }).loose()),
  me: z.coerce.number().int().nullable().default(null),
  isAdmin: booleanValueSchema.default(false),
}).loose();

export const buyerFeedSchema = z.object({
  configured: booleanValueSchema.default(false),
  sinceAt: nullableEpochSchema,
  buyers: z.array(z.object({
    id: z.coerce.string(),
    name: z.string().default('Someone'),
    product: z.string().default(''),
    at: nullableEpochSchema,
  }).loose()).default([]),
  max: countSchema.default(300),
  mentioned: z.array(z.coerce.string()).default([]),
  messages: z.array(z.object({
    id: z.coerce.string(),
    type: z.literal('msg'),
    text: z.string(),
    from: z.string().default('Staff'),
    at: nullableEpochSchema,
  }).loose()).default([]),
}).loose();

const salesTvMessageSchema = z.object({
  id: z.coerce.string(),
  type: z.literal('msg'),
  text: z.string(),
  from: z.string().default('Staff'),
  at: nullableEpochSchema,
}).loose();

const salesShoutoutSchema = z.object({
  id: z.coerce.string(),
  type: z.undefined().optional(),
  name: z.string().default('Someone'),
  product: z.string().default(''),
  at: nullableEpochSchema,
  mentionedAt: nullableEpochSchema,
}).loose();

export const salesMentionSchema = z.union([salesTvMessageSchema, salesShoutoutSchema]);
export const salesMentionsPayloadSchema = z.object({
  mentions: z.array(salesMentionSchema).default([]),
}).loose();

export const runtimeSchema = z.object({ localDemo: booleanValueSchema.default(false) }).loose();

export const messageUpdateSchema = z.object({
  id: z.coerce.number().int(),
  flagged: booleanValueSchema.optional(),
  highlighted: booleanValueSchema.optional(),
  pinned: booleanValueSchema.optional(),
  answered: booleanValueSchema.optional(),
  deleted: booleanValueSchema.optional(),
  reply: z.string().optional(),
}).loose();

export const messageThreadSchema = z.object({
  id: z.coerce.number().int(),
  who: z.enum(['mod', 'attendee']),
  name: z.string().nullable().default(null),
  text: z.string(),
}).loose();

export const replyResultSchema = z.object({
  id: z.coerce.number().int(),
  replied: booleanValueSchema,
  answered: booleanValueSchema,
  zoomed: booleanValueSchema,
  to: z.enum(['everyone', 'private']),
  via: z.enum(['own', 'shared']).nullable(),
  webinar: booleanValueSchema,
}).loose();

export const messageActionResultSchema = z.object({
  id: z.coerce.number().int(),
  flagged: booleanValueSchema.optional(),
  highlighted: booleanValueSchema.optional(),
  answered: booleanValueSchema.optional(),
  deleted: booleanValueSchema.optional(),
  queue: pinQueueSchema.optional(),
}).loose();

export const pinAnswerResultSchema = z.object({ answered: z.coerce.number().int(), queue: pinQueueSchema }).loose();
export const roomBotResultSchema = z.object({ ok: booleanValueSchema, joined: booleanValueSchema, botName: z.string().optional() }).loose();
export const announcementResultSchema = z.object({
  sent: countSchema,
  total: countSchema,
  results: z.array(z.object({
    eventId: z.coerce.number().int(),
    room: z.string(),
    ok: booleanValueSchema,
    error: z.string().optional(),
  }).loose()),
}).loose();
export const snippetSaveResultSchema = z.object({ ok: booleanValueSchema, id: z.coerce.number().int(), already: booleanValueSchema }).loose();
export const mentionMutationResultSchema = z.object({ ok: booleanValueSchema, mentions: z.array(z.unknown()).optional() }).loose();

export const adminUserSchema = z.object({
  id: z.coerce.number().int(),
  username: z.string().default(''),
  first_name: nullableTextSchema,
  last_name: nullableTextSchema,
  email: nullableTextSchema,
  can_moderate: booleanValueSchema.default(false),
  can_stats: booleanValueSchema.default(false),
  can_data: booleanValueSchema.default(false),
  can_team: booleanValueSchema.default(false),
  can_display: booleanValueSchema.default(false),
  can_av: booleanValueSchema.default(false),
  can_events: booleanValueSchema.default(false),
  is_admin: booleanValueSchema.default(false),
  approved: booleanValueSchema.default(false),
  zoom_owner_id: z.coerce.number().int().nullable().default(null),
  reset_requested: booleanValueSchema.default(false),
  created_at: nullableEpochSchema,
}).loose();

export const adminUsersSchema = z.array(adminUserSchema);

export const adminHealthSchema = z.object({
  ranAt: z.coerce.number(),
  ok: booleanValueSchema,
  checks: z.array(z.object({
    key: z.string(),
    label: z.string(),
    status: z.enum(['ok', 'warn', 'fail', 'info']),
    detail: z.string().default(''),
  }).loose()),
}).loose();

export const adminUsageSchema = z.object({
  limit: z.coerce.number().positive(),
  ratePerHour: z.coerce.number().nonnegative(),
  users: z.array(z.object({
    id: z.coerce.number().int(),
    name: z.string(),
    email: z.string(),
    cost: z.coerce.number().nonnegative(),
    hours: z.coerce.number().nonnegative(),
    live: z.coerce.number().int().nonnegative(),
    over: booleanValueSchema,
    cycleStart: z.coerce.number(),
  }).loose()),
}).loose();

export const adminSignupCodeSchema = z.object({ code: z.string().min(1) }).loose();
export const adminInviteResultSchema = z.object({
  ok: booleanValueSchema,
  invite: z.object({ url: z.string(), login: z.string(), password: z.string() }).loose(),
}).loose();
export const adminZoomGrantResultSchema = z.object({ ok: booleanValueSchema, granted: booleanValueSchema }).loose();
export const adminRecallLimitResultSchema = z.object({ ok: booleanValueSchema, limit: z.coerce.number().positive() }).loose();

export const notificationSchema = z.object({
  id: z.number(),
  kind: z.string(),
  body: z.string(),
  created_at: z.coerce.number(),
  read_at: z.coerce.number().nullable().default(null),
  from_user_id: z.coerce.number().nullable().default(null),
  subject_user_id: z.coerce.number().nullable().default(null),
  from_first: z.string().nullable().default(null),
  from_last: z.string().nullable().default(null),
  from_name: z.string().nullable().default(null),
}).loose();

export const notificationsSchema = z.object({
  items: z.array(notificationSchema),
  unread: z.number(),
}).loose();

export const coverageStateSchema = z.object({
  rooms: z.array(z.object({
    id: z.coerce.number(),
    name: z.string(),
    is_vip: booleanValueSchema.default(false),
    active: booleanValueSchema.default(false),
  }).loose()),
  members: z.array(z.object({
    id: z.coerce.number(),
    name: z.string(),
    online: booleanValueSchema.default(false),
  }).loose()),
  coverage: z.array(z.object({
    event_id: z.coerce.number(),
    user_id: z.coerce.number(),
    bot_name: z.string().default(''),
    status: z.string().default(''),
    has_bot: booleanValueSchema.default(false),
  }).loose()),
  me: z.object({
    id: z.coerce.number(),
    mine: z.array(z.object({
      event_id: z.coerce.number(),
      bot_name: z.string().default(''),
      active: booleanValueSchema.default(false),
    }).loose()).default([]),
  }).loose().optional(),
}).loose();

export const dataArchiveSummarySchema = z.object({
  totals: z.object({
    events: countSchema,
    withChat: countSchema,
    messages: countSchema,
    replyPairs: countSchema,
  }).loose(),
  events: z.array(z.object({
    id: z.coerce.number().int(),
    room_name: nullableTextSchema,
    topic: nullableTextSchema,
    event_type: nullableTextSchema,
    scheduled_at: nullableEpochSchema,
    status: nullableTextSchema,
    archived: booleanValueSchema.default(false),
    zoom_id: z.coerce.string(),
    messages: countSchema,
    first_ts: nullableEpochSchema,
    last_ts: nullableEpochSchema,
    reply_pairs: countSchema,
  }).loose()),
}).loose();

const insightFactsSchema = z.object({
  messages: countSchema,
  questions: countSchema,
  unanswered: countSchema,
  answered: countSchema,
  uniquePeople: countSchema,
  withEmail: countSchema,
}).loose();

const insightPersonSchema = z.object({
  name: z.string(),
  email: nullableTextSchema,
  messageId: z.coerce.number().int(),
  asked: z.string(),
  answered: booleanValueSchema,
  contactable: booleanValueSchema,
}).loose();

const insightClusterSchema = z.object({
  label: z.string().optional(),
  question: z.string().optional(),
  detail: z.string().optional(),
  askedTimes: countSchema,
  variants: z.array(z.string()).default([]),
  people: z.array(insightPersonSchema).default([]),
  unanswered: countSchema.default(0),
}).loose();

const insightReportSchema = z.object({
  generatedAt: z.coerce.number(),
  model: z.string(),
  truncated: booleanValueSchema,
  analysedMessages: countSchema,
  droppedSignal: countSchema.default(0),
  facts: insightFactsSchema,
  topQuestions: z.array(insightClusterSchema).default([]),
  requests: z.array(insightClusterSchema).default([]),
  buying: z.array(insightClusterSchema).default([]),
  money: z.array(insightClusterSchema).default([]),
  wants: z.array(insightClusterSchema).default([]),
  important: z.array(z.object({
    question: z.string(),
    whyItMatters: z.string(),
    who: insightPersonSchema,
  }).loose()).default([]),
  presentation: z.array(z.string()).default([]),
  slideDeck: z.array(z.string()).default([]),
  engagement: z.array(z.string()).default([]),
  sentiment: z.object({ overall: z.string(), note: z.string() }).loose().nullable().default(null),
}).loose();

export const insightStatusSchema = z.object({
  room: nullableTextSchema,
  aiReady: booleanValueSchema,
  status: z.enum(['none', 'building', 'ready', 'error']),
  createdAt: nullableEpochSchema.optional(),
  error: nullableTextSchema.optional(),
  model: nullableTextSchema.optional(),
  facts: insightFactsSchema,
  report: insightReportSchema.nullable().default(null),
}).loose();

export const dataPurgeResultSchema = z.object({
  id: z.coerce.number().int(),
  purged: z.literal(1),
  removed_messages: countSchema,
}).loose();

export const insightStartSchema = z.object({
  status: z.enum(['building', 'ready']),
}).loose();

const nullableNumberSchema = z.union([z.number(), z.string()]).transform(value => Number(value)).nullable().default(null);

const statsMoverSchema = z.object({
  id: z.coerce.number().int(),
  name: z.string(),
  delta: z.coerce.number(),
}).loose();

export const statsPayloadSchema = z.object({
  now: z.coerce.number(),
  rooms: z.array(z.object({
    id: z.coerce.number().int(),
    name: z.string(),
    type: nullableTextSchema,
    is_vip: booleanValueSchema.default(false),
    color: z.string().default('#6b7280'),
    count: nullableNumberSchema,
    baseline: nullableNumberSchema,
    delta: nullableNumberSchema,
    percent: nullableNumberSchema,
    isNew: booleanValueSchema.default(false),
    peak: nullableNumberSchema,
    samples: z.array(z.object({ ts: z.coerce.number(), count: z.coerce.number() }).loose()).default([]),
    updated: nullableEpochSchema,
    hasBot: booleanValueSchema.default(false),
    ended: booleanValueSchema.default(false),
    demo: booleanValueSchema.default(false),
    liveSince: nullableEpochSchema,
    settling: booleanValueSchema.default(false),
    measureFrom: nullableEpochSchema,
    countSource: nullableTextSchema,
    syncError: nullableTextSchema,
    statsStart: nullableEpochSchema,
    anchored: booleanValueSchema.default(false),
    pending: booleanValueSchema.default(false),
  }).loose()),
  totals: z.object({
    inRoomsNow: z.coerce.number(),
    baseTotal: z.coerce.number(),
    deltaTotal: z.coerce.number(),
    peak: z.coerce.number(),
    peakTs: nullableEpochSchema,
    hourStart: nullableEpochSchema,
    vipRooms: z.coerce.number(),
    vipNow: z.coerce.number(),
    biggestDrop: statsMoverSchema.nullable().default(null),
    biggestGain: statsMoverSchema.nullable().default(null),
  }).loose(),
  demo: booleanValueSchema.default(false),
  canEditStart: booleanValueSchema.default(false),
}).loose();

export const statsSalesPayloadSchema = z.object({
  configured: booleanValueSchema,
  at: nullableEpochSchema,
  error: nullableTextSchema,
  sinceAt: nullableEpochSchema,
  products: z.array(z.object({
    id: z.string(),
    label: z.string(),
    count: countSchema,
    delta: z.coerce.number(),
  }).loose()),
  total: countSchema,
  totalDelta: z.coerce.number(),
});

export const displaySettingsSchema = z.object({
  chatSize: z.coerce.number().min(10).max(60).default(18),
  highlightSize: z.coerce.number().min(12).max(72).default(22),
  pinnedSize: z.coerce.number().min(14).max(84).default(26),
  senderSize: z.coerce.number().min(9).max(28).default(14),
  avatarSize: z.coerce.number().min(30).max(96).default(44),
  reactions: booleanValueSchema.default(true),
  vipBadges: booleanValueSchema.default(true),
  autoScroll: booleanValueSchema.default(true),
  pinnedVisible: booleanValueSchema.default(true),
  theme: z.preprocess(value => value === 'forest' || value === 'red' ? 'dark' : value, z.enum(['dark', 'dark-blue', 'blue', 'blue-gray']).default('dark')),
  avatars: booleanValueSchema.default(true),
  avatarColorMode: z.enum(['auto', 'room', 'solid']).default('auto'),
  avatarSolidColor: z.string().regex(/^#[0-9a-f]{6}$/i).default('#4a90d9'),
  aiHighlight: booleanValueSchema.default(false),
  aiSelectivity: z.enum(['generous', 'selective', 'very-selective']).default('selective'),
}).loose();

export const displaySizeSettingsSchema = displaySettingsSchema.pick({
  chatSize: true,
  highlightSize: true,
  pinnedSize: true,
  senderSize: true,
  avatarSize: true,
});

export const avPresetSchema = z.object({
  id: z.coerce.number().int(),
  name: z.string(),
  data: displaySizeSettingsSchema,
}).loose();
export const avPresetsSchema = z.array(avPresetSchema);
export const avPresetResultSchema = z.object({ ok: z.literal(true), preset: avPresetSchema }).loose();
export const kioskKeySchema = z.object({ key: z.string().min(1) });
export const tvLaunchSchema = z.object({ launched: booleanValueSchema });
export const tvCloseSchema = z.object({ closed: booleanValueSchema });
export const curatedPeopleSchema = z.object({
  people: z.array(z.object({ name: z.string().optional(), email: z.string().optional(), photo: z.string().min(1) }).loose()).default([]),
}).loose();

export const statsRoomMutationSchema = z.object({
  ok: z.literal(true),
  stats_start: nullableEpochSchema,
}).loose();

export const statsRoomsResultSchema = z.object({
  rooms: z.array(z.object({
    room: z.string(),
    ok: z.boolean().optional(),
    count: nullableNumberSchema.optional(),
    reason: z.string().optional(),
  }).loose()),
}).loose();

export const statsFreshResultSchema = statsRoomsResultSchema.extend({ ok: z.literal(true) }).loose();

export const salesResetResultSchema = z.object({ ok: z.literal(true), sinceAt: z.coerce.number() }).loose();

export const guestAccessSchema = z.object({
  token: z.string().min(1),
  passcodeSet: booleanValueSchema.default(false),
});

export const guestLoginSchema = z.object({
  ok: z.literal(true),
  already: z.boolean().optional(),
}).loose();

export const okResultSchema = z.object({ ok: z.literal(true) }).loose();
